import React, { useCallback, useEffect, useRef, useState } from 'react';
import './tokens.css';
import './compass.css';
import {
    useGetPortalProjectQuery,
    useSetStageDueMutation,
} from '../../../redux/api/compassPortal';
import { useGetUserTypeQuery } from '../../../redux/api/user';
import ProjectsList from './ProjectsList';
import DataCollectionTracker from './DataCollectionTracker';
import ProcessingWorkspace from './ProcessingWorkspace';
import ValidationWorkspace from './ValidationWorkspace';
import StagePanel from './StagePanel';
import ModellingWorkspace from './ModellingWorkspace';
import DeliveryWorkspace from './DeliveryWorkspace';
import AgentPanel from './AgentPanel';
import CompassLoader from './CompassLoader';

// Compass Portal — ONE agent-guided, staged flow. A project opens into a workspace
// with a persistent per-project agent panel beside it; the stage rail (Tobi's 5
// stages) switches the working surface. The agent walks whoever is on the project
// through each step in plain language, and the conversation persists per project
// across users. Talks straight to co-backend-v2 through the shared RTK Query slice
// (bearer auth via baseQuery) — the project object itself is a live query, so every
// mutation that invalidates PortalProjects refreshes the header/stages by itself.

export const STAGE_ORDER = ['data-collection', 'data-processing', 'data-validation', 'data-modelling', 'compass-delivery'];
export const STAGE_NAMES = {
    'data-collection': 'Data Collection', 'data-processing': 'Data Processing',
    'data-validation': 'Data Validation', 'data-modelling': 'Modelling', 'compass-delivery': 'Delivery',
};

export default function CompassPortal({ initialProjectId, initialStageId }) {
    const [projectId, setProjectId] = useState(initialProjectId || null);
    const [stageId, setStageId] = useState(initialStageId || 'data-collection');
    const [agentOpen, setAgentOpen] = useState(false);
    const [agentMounted, setAgentMounted] = useState(false); // once opened, keep it mounted
    const [bravoTick, setBravoTick] = useState(0); // bump so workspaces re-read after Bravo acts
    // A deep link arrives without stage context — resolve the stage once the project loads.
    const needsStageResolve = useRef(Boolean(initialProjectId && !initialStageId));

    const { data: userType } = useGetUserTypeQuery();
    const { data: project, refetch: refetchProject } =
        useGetPortalProjectQuery({ projectId }, { skip: !projectId });
    const [setStageDue] = useSetStageDueMutation();

    useEffect(() => {
        if (needsStageResolve.current && project?.stages) {
            const cur = project.stages.find((s) => s.status !== 'complete');
            setStageId(cur?.id || 'data-collection');
            needsStageResolve.current = false;
        }
    }, [project]);

    const refreshProject = useCallback(() => {
        refetchProject();
        setBravoTick((n) => n + 1); // workspaces re-read sources without a remount
    }, [refetchProject]);

    const openProject = useCallback((p) => {
        setProjectId(p.project_id);
        // open at the first stage that isn't complete (resume where the project is)
        const cur = (p.stages || []).find((s) => s.status !== 'complete');
        setStageId(cur?.id || 'data-collection');
    }, []);
    const backToList = useCallback(() => { setProjectId(null); setStageId('data-collection'); }, []);

    const onStageComplete = useCallback((completedId, nextId) => {
        refreshProject();
        if (nextId) setStageId(nextId);
    }, [refreshProject]);

    const stageStatus = (id) => (project?.stages || []).find((s) => s.id === id)?.status;
    const stageDue = (id) => (project?.stages || []).find((s) => s.id === id)?.due_date || '';

    // Editing a stage deadline cascades later stages (server shifts them by the delta;
    // the mutation invalidates PortalProjects so the row refreshes with the cascade).
    const setDue = async (id, due) => {
        try { await setStageDue({ projectId, stage_id: id, due_date: due }).unwrap(); } catch { /* keep */ }
    };

    const workspace = () => {
        // refreshSignal bumps after Bravo acts → the workspace re-fetches WITHOUT a
        // full remount (remounting the sibling subtree was racing the agent drawer shut).
        const common = { project, stageId, onBack: backToList, onComplete: onStageComplete, refreshSignal: bravoTick };
        if (stageId === 'data-collection') return <DataCollectionTracker {...common} />;
        if (stageId === 'data-processing') return <ProcessingWorkspace {...common} />;
        if (stageId === 'data-validation') return <ValidationWorkspace {...common} stageName={STAGE_NAMES[stageId]} />;
        if (stageId === 'data-modelling') return <ModellingWorkspace {...common} />;
        if (stageId === 'compass-delivery') return <DeliveryWorkspace {...common} />;
        return <StagePanel {...common} stageName={STAGE_NAMES[stageId]} />;
    };

    if (!projectId) {
        return (
            <div className="cp">
                <div className="cp-topbar">
                    <div>
                        <p className="cp-eyebrow">Compass Portal</p>
                        <h1 className="cp-title">Projects</h1>
                        <p className="cp-sub">Your Compass projects — the agent walks you through each one, from data collection to first model.</p>
                    </div>
                </div>
                <ProjectsList onOpen={openProject} isAdmin={userType === 'Admin'} />
            </div>
        );
    }

    if (!project) return <div className="cp"><CompassLoader /></div>;

    return (
        <div className="cp">
            <div className="cp-topbar">
                <div>
                    <p className="cp-eyebrow">Compass Portal</p>
                    <h1 className="cp-title">{project.project_name || 'Project'}</h1>
                    <p className="cp-sub">{project.client_name}</p>
                </div>
            </div>

            <nav className="cp-crumbs" aria-label="Breadcrumb">
                <button onClick={backToList}>Projects</button>
                <span>/</span>
                <span className="cp-crumb-cur">{project.project_name}</span>
                <span>/</span>
                <span className="cp-crumb-cur">{STAGE_NAMES[stageId]}</span>
            </nav>

            {/* Tobi's 5 stages, always visible — the agent-led spine */}
            <div className="cp-rail" role="tablist" aria-label="Stages">
                {STAGE_ORDER.map((id, i) => (
                    <button key={id} role="tab" aria-current={stageId === id} onClick={() => setStageId(id)}>
                        <span className="cp-rail-num">{i + 1}</span>
                        {STAGE_NAMES[id]}
                        {stageStatus(id) === 'complete' && <span className="cp-rail-tick">✓</span>}
                    </button>
                ))}
            </div>

            {/* Cascading deadlines — set a due date per stage; slip one and the rest shift */}
            <div className="cp-deadlines">
                <span className="cp-deadlines-hint">Deadlines cascade — move one and later stages shift.</span>
                {STAGE_ORDER.map((id) => (
                    <label key={id} className="cp-deadline">
                        <span>{STAGE_NAMES[id]}</span>
                        <input type="date" value={stageDue(id)} onChange={(e) => setDue(id, e.target.value)} />
                    </label>
                ))}
            </div>

            <div className="cp-work-main" key={stageId}>{workspace()}</div>

            {/* The agent is a drawer, not a permanent column — the workspace gets the
                full width, and you open the agent when you want it. Once opened it stays
                MOUNTED (just hidden when closed) so closing the drawer mid-request never
                cancels Bravo's in-flight work — the reply lands in the same live panel. */}
            {!agentOpen && (
                <button className="cp-agent-fab" onClick={() => { setAgentMounted(true); setAgentOpen(true); }}>
                    <span className="cp-agent-dot" /> Speak to Bravo
                </button>
            )}
            {agentOpen && <div className="cp-drawer-backdrop" onClick={() => setAgentOpen(false)} />}
            {agentMounted && (
                <div className={`cp-agent-drawer${agentOpen ? '' : ' cp-agent-drawer--hidden'}`} role="dialog"
                    aria-label="Compass agent" aria-hidden={!agentOpen}>
                    <button className="cp-agent-close" onClick={() => setAgentOpen(false)} aria-label="Close agent">✕</button>
                    <AgentPanel projectId={project.project_id} stageId={stageId} stageName={STAGE_NAMES[stageId]}
                        onActed={refreshProject} />
                </div>
            )}
        </div>
    );
}
